import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  makeSyncReduxWithLocalStorageObserver,
  restoreFromLocalStorage,
} from 'app/dataManagement/syncReduxStateWithLocalStorage';
import { Size } from 'app/interfaces/Image';
import {
  LoadSharedUserConfigAction,
  SHARED_USER_CONFIG_LOADED,
} from 'app/redux/sharedUserConfig/actions';
import observeStore from 'app/utils/redux/observeStore';
import { Store } from 'redux';

export const VIEWER_CONFIGURATION_LOCAL_STORAGE_KEY = 'redux.viewerConfiguration';

export type MonitorIdentifier = 'NOT_ACCESSIBLE' | string;

export interface ViewerConfigurationState {
  syncToPACSOnValidate: boolean;
  noXRayDeletion: boolean;
  monitorsDimensions?: Record<MonitorIdentifier, Size>; // In millimeters
  realSizeMeasurementDefaultLength?: number;
  isAIAnalysisDisabled: boolean;
  isProcessingFineTuningEnabled: boolean;
}

export type StoreWithViewerConfiguration = { viewerConfiguration: ViewerConfigurationState };

const configurationInitialState = () =>
  ({
    syncToPACSOnValidate: true,
    noXRayDeletion: false,
    monitorsDimensions: {},
    realSizeMeasurementDefaultLength: undefined,
    isAIAnalysisDisabled: false,
    isProcessingFineTuningEnabled: false,
    ...restoreFromLocalStorage(VIEWER_CONFIGURATION_LOCAL_STORAGE_KEY),
  } as ViewerConfigurationState);

const viewerConfigurationSlice = createSlice({
  name: 'viewerConfiguration',
  initialState: configurationInitialState,
  reducers: {
    addMonitorDimensions: (
      state,
      action: PayloadAction<{ monitorIdentifier: MonitorIdentifier; monitorDimensions: Size }>
    ) => {
      if (!state.monitorsDimensions) state.monitorsDimensions = {};
      state.monitorsDimensions[action.payload.monitorIdentifier] = action.payload.monitorDimensions;
    },
    toggleNoXRayDeletion: {
      prepare: (noXRayDeletion: boolean) => {
        return {
          payload: noXRayDeletion,
          meta: { syncWithUserConfigurationAPI: 'noXRayDeletion' },
        };
      },
      reducer: (state, action: PayloadAction<boolean>) => {
        state.noXRayDeletion = action.payload;
      },
    },
    updateSyncToPACSOnValidate: (state, action: PayloadAction<boolean>) => {
      state.syncToPACSOnValidate = action.payload;
    },
    setRealSizeMeasurementDefaultLength: (state, action: PayloadAction<number | undefined>) => {
      state.realSizeMeasurementDefaultLength = action.payload;
    },
    toggleAIAnalysisDisabled: (state, action: PayloadAction<boolean>) => {
      state.isAIAnalysisDisabled = action.payload;
    },
    toggleProcessingFineTuningEnabled: {
      prepare: (isEnabled: boolean) => {
        return {
          payload: isEnabled,
          meta: { syncWithUserConfigurationAPI: 'isProcessingFineTuningEnabled' },
        };
      },
      reducer: (state, action: PayloadAction<boolean>) => {
        state.isProcessingFineTuningEnabled = action.payload;
      },
    },
  },

  extraReducers: (builder) => {
    builder.addCase(SHARED_USER_CONFIG_LOADED, (state, action: LoadSharedUserConfigAction) => {
      state.noXRayDeletion = action.payload.noXRayDeletion;
      if (action.payload.isProcessingFineTuningEnabled !== undefined) {
        state.isProcessingFineTuningEnabled = action.payload.isProcessingFineTuningEnabled;
      }
    });
  },
});

// Export actions
export const {
  addMonitorDimensions,
  toggleNoXRayDeletion,
  updateSyncToPACSOnValidate,
  setRealSizeMeasurementDefaultLength,
  toggleAIAnalysisDisabled,
  toggleProcessingFineTuningEnabled,
} = viewerConfigurationSlice.actions;

// Export selectors
export const selectViewerConfiguration = (state: StoreWithViewerConfiguration) =>
  state.viewerConfiguration;
export const selectNoXRayDeletion = (state: StoreWithViewerConfiguration) =>
  selectViewerConfiguration(state).noXRayDeletion;
export const selectAIAnalysisDisabled = (state: StoreWithViewerConfiguration) =>
  selectViewerConfiguration(state).isAIAnalysisDisabled;
export const selectProcessingFineTuningEnabled = (state: StoreWithViewerConfiguration) =>
  selectViewerConfiguration(state).isProcessingFineTuningEnabled;

type StateWithViewerConfiguration = {
  viewerConfiguration: ViewerConfigurationState;
};

export const attachViewerConfigurationWithLocalStorageSynchronizationObserver = (
  store: Store<StateWithViewerConfiguration>
) => {
  observeStore(
    store,
    selectViewerConfiguration,
    makeSyncReduxWithLocalStorageObserver(VIEWER_CONFIGURATION_LOCAL_STORAGE_KEY)
  );
};

// Export reducer
const viewerConfigurationReducer = viewerConfigurationSlice.reducer;
export default viewerConfigurationReducer;
